import { Link, useLocation } from "react-router-dom";
import {
  Home,
  Briefcase,
  FileText,
  Building2,
  UserCircle,
  Settings,
  ChevronUp,
  CheckSquare,
  KanbanSquare,
  Search,
  Flag,
  Trophy,
  Users,
  ScrollText,
  Star,
  PlusCircle,
  Bell,
  Calendar,
  MessageSquare,
  HeartHandshake,
  Shield,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuthUser, useHasRole } from "@/stores/authStore";
import { useLogout } from "@/features/auth";
// Deep imports keep feature UI components out of the main bundle
import { useChatUnreadCount } from "@/features/chat/hooks/useChatThreads";
import { useUnreadCount } from "@/features/notifications/hooks/useNotifications";
import type { UserRole } from "@/types/user.types";
import { cn } from "@/lib/utils";

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: UserRole[];
}

// Based on wireframes: student/dashboard.html
const studentNavItems: NavItem[] = [
  { title: "Dashboard", url: "/dashboard", icon: Home },
  { title: "My Profile", url: "/profile", icon: UserCircle },
  { title: "Browse Openings", url: "/openings", icon: Search },
  { title: "My Applications", url: "/applications", icon: FileText },
  { title: "Success Stories", url: "/success-stories", icon: Trophy },
  { title: "Calendar", url: "/calendar", icon: Calendar },
  { title: "Messages", url: "/messages", icon: MessageSquare },
  { title: "Notifications", url: "/notifications", icon: Bell },
];

// Based on wireframes: hr/dashboard.html, hr/enterprises.html
const hrNavItems: NavItem[] = [
  { title: "Dashboard", url: "/hr/dashboard", icon: Home },
  { title: "Enterprises", url: "/hr/enterprises", icon: Building2 },
  { title: "Job Approvals", url: "/hr/job-approvals", icon: CheckSquare },
  { title: "Job Openings", url: "/openings", icon: Briefcase },
  { title: "Contact Requests", url: "/hr/interests", icon: HeartHandshake },
  { title: "Pipeline", url: "/hr/pipeline", icon: KanbanSquare },
  { title: "Students", url: "/hr/students", icon: Users },
  { title: "Success Stories", url: "/success-stories", icon: Trophy },
  { title: "Calendar", url: "/hr/calendar", icon: Calendar },
  { title: "Messages", url: "/hr/messages", icon: MessageSquare },
  { title: "Notifications", url: "/notifications", icon: Bell },
];

// Based on wireframes: enterprise/post-job.html
const enterpriseNavItems: NavItem[] = [
  { title: "Dashboard", url: "/enterprise/dashboard", icon: Home },
  { title: "Post a Job", url: "/enterprise/jobs/create", icon: PlusCircle },
  { title: "My Jobs", url: "/enterprise/jobs", icon: Briefcase },
  { title: "Search Talent", url: "/talent", icon: Search },
  { title: "Watchlist", url: "/enterprise/watchlist", icon: Star },
  { title: "Pipeline", url: "/enterprise/pipeline", icon: KanbanSquare },
  { title: "Calendar", url: "/enterprise/calendar", icon: Calendar },
  { title: "Messages", url: "/enterprise/messages", icon: MessageSquare },
  { title: "Notifications", url: "/notifications", icon: Bell },
];

// Based on wireframes: admin/dashboard.html
const adminNavItems: NavItem[] = [
  { title: "Dashboard", url: "/admin", icon: Home },
  { title: "Users", url: "/admin/users", icon: Users },
  { title: "Enterprises", url: "/hr/enterprises", icon: Building2 },
  { title: "Job Approvals", url: "/hr/job-approvals", icon: CheckSquare },
  { title: "Job Openings", url: "/openings", icon: Briefcase },
  { title: "Roles & Permissions", url: "/admin/roles", icon: Shield },
  { title: "System Config", url: "/admin/system-config", icon: Settings },
  { title: "Feature Flags", url: "/admin/feature-flags", icon: Flag },
  { title: "Success Stories", url: "/admin/success-stories", icon: Trophy },
  { title: "Audit Log", url: "/hr/audit-log", icon: ScrollText },
  { title: "Notifications", url: "/notifications", icon: Bell },
];

export function AppSidebar() {
  const location = useLocation();
  const user = useAuthUser();
  const { mutate: logout } = useLogout();
  const isStudent = useHasRole(["STUDENT"]);
  const isHR = useHasRole(["HR_STAFF"]);
  const isEnterprise = useHasRole(["ENTERPRISE_RECRUITER"]);
  const isAdmin = useHasRole(["ADMIN"]);
  const chatUnreadCount = useChatUnreadCount();
  const { data: notifUnreadCount = 0 } = useUnreadCount();

  // Determine which nav items to show based on role
  let navItems: NavItem[] = [];
  let profileUrl = "/profile";

  if (isAdmin) {
    navItems = adminNavItems;
    profileUrl = "/admin/profile";
  } else if (isHR) {
    navItems = hrNavItems;
    profileUrl = "/hr/profile";
  } else if (isEnterprise) {
    navItems = enterpriseNavItems;
    profileUrl = "/enterprise/profile";
  } else if (isStudent) {
    navItems = studentNavItems;
    profileUrl = "/profile";
  }

  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  return (
    <Sidebar collapsible="icon" className="border-r border-[#ececec] bg-white">
      <SidebarHeader className="border-b border-[#eef0f2] pb-4">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="hover:bg-transparent">
              <Link to="/" className="flex flex-col items-start gap-0">
                <img
                  src="/zencube-logo-dark.png"
                  alt="ZenCube"
                  className="h-auto w-full max-w-[220px]"
                />
                <span className="mt-2 text-[11px] font-medium tracking-wide text-[#9a9a9a]">
                  Talent Registry
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="px-2 pt-4">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const isActive =
                  location.pathname === item.url || location.pathname.startsWith(`${item.url}/`);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      className={cn(
                        "relative ml-[-3px] border-l-[3px] border-transparent px-3 py-2.5 text-[14px] text-[#5a6068] transition-colors hover:bg-[#f4f6f9] hover:text-[#2b2b2b]",
                        isActive &&
                          "border-l-[#2b5b96] bg-[#ebf2fa] font-medium text-[#2b5b96] hover:bg-[#ebf2fa] hover:text-[#2b5b96]"
                      )}
                    >
                      <Link to={item.url} aria-current={isActive ? "page" : undefined}>
                        <item.icon
                          className={cn(
                            "h-[18px] w-[18px] text-[#8a9097]",
                            isActive && "text-[#2b5b96]"
                          )}
                          aria-hidden="true"
                        />
                        <span>{item.title}</span>
                        {item.title === "Messages" && chatUnreadCount > 0 && (
                          <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#2b5b96] px-1 text-[10px] font-semibold text-white">
                            {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                          </span>
                        )}
                        {item.title === "Notifications" && notifUnreadCount > 0 && (
                          <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#2b5b96] px-1 text-[10px] font-semibold text-white">
                            {notifUnreadCount > 99 ? "99+" : notifUnreadCount}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-[#eef0f2]">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-[#f4f6f9] data-[state=open]:text-[#2b2b2b]"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user?.avatarUrl} alt={user?.name} />
                    <AvatarFallback className="rounded-lg bg-[#f2f3f5] text-[#6a6a6a]">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold text-[#2b2b2b]">{user?.name}</span>
                    <span className="truncate text-xs text-[#8a8a8a]">{user?.email}</span>
                  </div>
                  <ChevronUp className="ml-auto size-4 text-[#8a9097]" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuItem asChild>
                  <Link to={profileUrl}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                  }}
                  className="text-destructive"
                >
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
